import { Link } from "react-router-dom";
import { Mail, ArrowUpRight, MoreHorizontal, Quote } from "lucide-react";
import SiteHead from "@/components/SiteHead";
import ChatWidget from "@/components/ChatWidget";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { mediaUrl } from "@/lib/api";
import {
  LandingHeader, LandingFooter, LandingStickyCta, landingLogoVars, yearlyPlans,
} from "@/components/LandingChrome";
import { BRAND_NAME } from "@/lib/brand";
import verifyBadge from "@/assets/verify.png";

const SOCIAL_META = [
  { key: "x", label: "X" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "youtube", label: "YouTube" },
];

function initials(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "MS";
}

function sameAsList(social) {
  return SOCIAL_META.map((s) => social?.[s.key]).filter(Boolean);
}

function VerifiedTick({ label = "Verified" }) {
  return (
    <img
      src={verifyBadge}
      alt={label}
      title={label}
      width={16}
      height={16}
      className="ms-verified"
      data-testid="company-person-verified"
    />
  );
}

export default function CompanyPersonPage({ role = "founder" }) {
  const cfg = useSiteConfig();
  const people = cfg.company_people || {};
  const person = role === "co-founder" ? people.co_founder : people.founder;
  const name = person?.name || (role === "co-founder" ? "Sharookh Anis Ahmad" : "Neel Varma");
  const legal = person?.legal_name || "";
  const email = person?.email || "";
  const bio = person?.bio || "";
  const headline = person?.headline || `${person?.role || (role === "co-founder" ? "Co-founder" : "Founder")} of ${BRAND_NAME}`;
  const photo = person?.photo_url ? mediaUrl(person.photo_url) : "";
  const banner = person?.banner_url ? mediaUrl(person.banner_url) : "";
  const social = person?.social || {};
  const ventures = Array.isArray(person?.ventures) ? person.ventures : [];
  const job = person?.role || (role === "co-founder" ? "Co-founder" : "Founder");
  const path = role === "co-founder" ? "/company/co-founder" : "/company/founder";
  const otherPath = role === "co-founder" ? "/company/founder" : "/company/co-founder";
  const otherLabel = role === "co-founder" ? "Meet the founder" : "Meet the co-founder";
  const ofLabel = role === "co-founder" ? "Co-founder of" : "Founder of";
  const plans = yearlyPlans(cfg.pricing?.plans);
  const websitePrice = plans.find((p) => p.id === "website")?.price || 1999;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const sameAs = sameAsList(social);
  const title = `${name} — ${job} of ${BRAND_NAME}`;
  const description = (headline || bio || `${name} is the ${job.toLowerCase()} of ${BRAND_NAME}, the Indian ecommerce website builder.`).slice(0, 160);
  const tags = ventures.map((v) => v.name).filter(Boolean).slice(0, 3);
  const personLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name,
    alternateName: legal || undefined,
    jobTitle: `${job}, ${BRAND_NAME}`,
    email: email || undefined,
    image: photo || undefined,
    url: origin ? `${origin}${path}` : undefined,
    sameAs: sameAs.length ? sameAs : undefined,
    worksFor: { "@type": "Organization", name: BRAND_NAME, url: origin || "https://merastartup.in" },
    affiliation: { "@type": "Organization", name: BRAND_NAME, url: origin || "https://merastartup.in" },
  };

  return (
    <div className="ms-landing min-h-screen font-sans antialiased overflow-x-hidden pb-16 md:pb-0" style={landingLogoVars(cfg)}>
      <SiteHead
        path={path}
        title={title}
        description={description}
        image={photo || banner || undefined}
        extraJsonLd={personLd}
      />
      <LandingHeader websitePrice={websitePrice} />

      <div className="max-w-[1100px] mx-auto px-5 lg:px-8 pt-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone-500 mb-4">
          <Link to="/" className="hover:text-stone-900">Home</Link>
          <span className="mx-2 text-stone-300">/</span>
          <Link to="/about" className="hover:text-stone-900">About</Link>
          <span className="mx-2 text-stone-300">/</span>
          <span className="text-stone-800">{job}</span>
        </p>

        <article className="rounded-[28px] overflow-hidden border border-black/10 bg-white shadow-sm" data-testid="company-person-card">
          <div className="relative h-52 sm:h-56 md:h-64 ms-profile-banner overflow-hidden">
            {banner ? (
              <img src={banner} alt="" className="absolute inset-0 w-full h-full object-cover object-center" data-testid="company-person-banner" />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-r from-stone-950/92 via-stone-950/55 to-stone-950/20" />
            <div className="relative z-10 h-full px-5 sm:px-8 py-5 flex flex-col justify-start gap-3 max-w-[46rem] pr-[1rem] sm:pr-8">
              <span className="inline-flex self-start px-3 py-1 rounded-md bg-[#FF8A2B] text-white text-[11px] font-bold tracking-wide">
                {job} of {BRAND_NAME}
              </span>
              <div>
                <h2 className="ms-serif text-3xl sm:text-4xl md:text-[2.6rem] font-bold text-white leading-[1.05]">
                  Scale a store. <em className="font-medium not-italic sm:italic text-[#FFD0A8]">Own the customer.</em>
                </h2>
                <p className="mt-2 hidden text-sm text-white/85 sm:block sm:text-base max-w-md">{headline}</p>
              </div>
            </div>
            {tags.length ? (
              <div className="hidden sm:flex absolute z-10 left-[11.5rem] right-6 bottom-3 flex-wrap justify-end gap-2">
                {tags.map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full border border-white/35 bg-black/35 text-[11px] font-bold text-white backdrop-blur-sm">{t}</span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="relative px-5 sm:px-8 pb-8">
            <div className="flex justify-end pt-3 min-h-[2.5rem]">
              {social.linkedin ? (
                <a href={social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-[#0A66C2] hover:opacity-80" data-testid="company-banner-linkedin">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 110-4.12 2.06 2.06 0 010 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.46c.98 0 1.77-.77 1.77-1.73V1.73C24 .77 23.21 0 22.23 0z" /></svg>
                </a>
              ) : null}
            </div>

            <div className="absolute left-5 sm:left-8 -top-16 sm:-top-[4.5rem] z-20 pointer-events-none">
              <div className="w-[7.25rem] h-[7.25rem] sm:w-36 sm:h-36 rounded-full border-[5px] border-white bg-[#FFEAD1] overflow-hidden shadow-md">
                {photo ? (
                  <img src={photo} alt={`${name}, ${job} of ${BRAND_NAME}`} className="h-full w-full object-cover" data-testid="company-person-photo" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center" data-testid="company-person-photo-fallback">
                    <span className="ms-serif text-3xl font-bold text-[#FF8A2B]">{initials(name)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-14 sm:pt-16">
              <h1 className="ms-serif text-3xl sm:text-4xl font-bold text-stone-900 flex items-center gap-1.5" data-testid="company-person-name">
                <span className="leading-none">{name}</span>
                <VerifiedTick />
              </h1>
              {legal ? (
                <p className="mt-1 text-sm text-stone-500" data-testid="company-person-legal">Legal name {legal}</p>
              ) : null}
              <p className="mt-2 text-stone-700 max-w-2xl">{headline}</p>
              <p className="mt-2 text-sm text-[#0A66C2] font-semibold">
                {job} · {BRAND_NAME} · Mumbai, India
              </p>
              {tags.length ? (
                <div className="mt-3 flex flex-wrap gap-1.5 sm:hidden" data-testid="company-person-tags-mobile">
                  {tags.map((t) => (
                    <span key={t} className="px-2.5 py-1 rounded-full border border-stone-300 bg-stone-50 text-[11px] font-bold text-stone-700">{t}</span>
                  ))}
                </div>
              ) : null}

              <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="company-person-socials">
                {email ? (
                  <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 text-white text-sm font-bold px-4 py-2 hover:bg-stone-800" data-testid="company-person-email">
                    <Mail className="w-4 h-4" /> {email}
                  </a>
                ) : null}
                <Link to="/" className="inline-flex items-center rounded-full border border-stone-900 text-stone-900 text-sm font-bold px-4 py-2 hover:bg-stone-50">View website</Link>
                <Link to="/about" className="inline-flex items-center rounded-full border border-stone-900 text-stone-900 text-sm font-bold px-4 py-2 hover:bg-stone-50">About</Link>
                <Link to={otherPath} className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-stone-300 text-stone-600 hover:bg-stone-50" aria-label={otherLabel}>
                  <MoreHorizontal className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {SOCIAL_META.map((s) => social[s.key] ? (
                  <a
                    key={s.key}
                    href={social[s.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold text-stone-600 hover:text-stone-900"
                    data-testid={`company-social-${s.key}`}
                  >
                    {s.label} <ArrowUpRight className="w-3 h-3" />
                  </a>
                ) : null)}
              </div>
            </div>
          </div>
        </article>
      </div>

      <section className="max-w-[800px] mx-auto px-5 lg:px-8 py-12">
        <div className="relative rounded-[28px] bg-white border border-black/10 p-7 sm:p-10 shadow-sm">
          <Quote className="w-8 h-8 text-[#FF8A2B]" />
          <div className="mt-4 space-y-4 text-lg leading-relaxed text-stone-700" data-testid="company-person-bio">
            {bio.split(/\n+/).filter(Boolean).map((para, i) => (
              <p key={i} className={i === 0 ? "first-letter:ms-serif first-letter:text-5xl first-letter:font-bold first-letter:text-[#FF8A2B] first-letter:mr-1 first-letter:float-left first-letter:leading-[0.85]" : ""}>{para}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 pb-20">
        <p className="text-[11px] font-bold uppercase tracking-widest text-stone-400">{ofLabel}</p>
        <h2 className="ms-serif mt-2 text-3xl font-bold">{name.split(" ")[0]}&apos;s companies &amp; studios</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="company-person-ventures">
          {ventures.map((v, i) => {
            const inner = (
              <>
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF8A2B]">{String(i + 1).padStart(2, "0")}</div>
                <div className="ms-serif mt-3 text-xl font-bold text-stone-900">{v.name}</div>
                {v.url ? <div className="mt-2 text-xs font-semibold text-stone-500 truncate">{v.url.replace(/^https?:\/\//, "")}</div> : null}
              </>
            );
            const cls = "rounded-[22px] border border-black/10 bg-white p-5 shadow-sm hover:-translate-y-0.5 transition-transform min-h-[8.5rem] flex flex-col";
            return v.url ? (
              <a key={`${v.name}-${i}`} href={v.url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`company-venture-${i}`}>{inner}</a>
            ) : (
              <div key={`${v.name}-${i}`} className={cls} data-testid={`company-venture-${i}`}>{inner}</div>
            );
          })}
        </div>
        <div className="mt-10 flex flex-wrap gap-4 text-sm font-semibold">
          <Link to="/about" className="text-orange-700 hover:text-orange-800 underline underline-offset-4" data-testid="company-about-link">About Mera Startup</Link>
          <Link to={otherPath} className="text-stone-600 hover:text-stone-900 underline underline-offset-4" data-testid="company-other-person-link">{otherLabel}</Link>
        </div>
      </section>

      <LandingFooter />
      <LandingStickyCta websitePrice={websitePrice} />
      <ChatWidget />
    </div>
  );
}
